import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  Column,
  CustomAction,
  DataTableComponent,
  NimmstaDataSource,
  EmptyTableComponent,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  DateTimePipe,
  NimmstaTimeAgoPipe,
} from '@nimmsta/ng-commons-lib/pipes';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { MdmService } from '../../services/mdm.service';
import { Mdm } from '../../models/mdm';
import { TranslatePipe } from '@ngx-translate/core';
import { createOrganizationFilter } from '../organization-filter.util';

_('mdm.title');

@Component({
  templateUrl: 'mdm.component.html',
  selector: 'app-mdm',
  imports: [
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    TranslatePipe,
  ],
  providers: [NimmstaTimeAgoPipe, DateTimePipe],
})
export class MdmComponent implements OnInit, OnChanges {
  private mdmService = inject(MdmService);
  private router = inject(Router);
  private timeago = inject(NimmstaTimeAgoPipe);
  private dateTimePipe = inject(DateTimePipe);

  deviceSource!: NimmstaDataSource<Mdm>;

  @Input() organizationId?: string;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<Mdm>;

  customActions: CustomAction<Mdm>[] = [];

  error?: HttpErrorResponse;

  get columns(): Column[] {
    return [
      new Column('name', 'mdm.name', { sortable: true }),
      new Column('occupyingWorker.family_name', 'user.family_name', {
        sortable: true,
      }),
      new Column('occupyingWorker.given_name', 'user.given_name', {
        sortable: true,
      }),
      new Column('lastConnection', 'mdm.lastConnection', {
        pipe: this.timeago,
        sortable: true,
        invertSorting: true,
      }),
      new Column('os', 'mdm.os', { sortable: false }),
      new Column('handheldSerial', 'mdm.handheldSerial', { sortable: false }),
      new Column('localIpAddress', 'mdm.localIpAddress', { sortable: false }),
      new Column('appVersion', 'mdm.appVersion', { sortable: false }),
      new Column('createdAt', 'createdAt', {
        pipe: this.dateTimePipe,
        sortable: true,
      }),
    ];
  }

  ngOnInit(): void {
    this.deviceSource = new NimmstaDataSource<Mdm>(this.mdmService);
    this.applyOrganizationFilter();
    const deleteAction = new CustomAction('delete', false, true);
    this.customActions.push(deleteAction);
    deleteAction.subscribe((id) => this.delete(id));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['organizationId'] ||
      changes['organizationId'].firstChange ||
      !this.deviceSource
    ) {
      return;
    }

    this.applyOrganizationFilter();
    this.tableComponent?.loadDataPage(true);
  }

  private applyOrganizationFilter(): void {
    this.deviceSource.defaultFilter = createOrganizationFilter(
      'organization.id',
      this.organizationId,
    );
  }

  show(id: string): void {
    this.router.navigate(['/devices/mdm', id]);
  }

  delete(id: string): void {
    const device = this.getSelectedObject(id)!;
    this.mdmService.delete(device).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  getSelectedObject(id: string): Mdm | undefined {
    return this.deviceSource.data?.content.filter((d) => id === d.id).pop();
  }

  emptyButtonClick(): void {
    this.router.navigate(['/users']);
  }
}
